// Castle-city art, for Lanternport (Moonmere's walled stone harbour, in Moonmere blue): crenellated walls and
// towers, the gatehouse on the road out, the keep on its rise, braziers, blue-glass stone lanterns, banner poles,
// a statue, market stalls with blue awnings, stone houses with slate roofs, and flagstone paving. Same Inkwell
// primitives as inkprops.ts, in ink units (a tile is 48), anchored at the feet. Hooked in from inkprops.ts
// (sprites), inkscene.ts (lights, anchors and the paving).
import ink from '../ink/ink.js';
import type { PropDef } from '../../../shared/zonebuild.js';
import type { Light } from './sky.js';

const I = ink as any;
const { INK, T, TAU, blob, oval, line, poly, sprite, shade, mulberry, rrect } = I;
type G = CanvasRenderingContext2D;
interface Spr { c: HTMLCanvasElement; ox: number; oy: number; w: number; h: number }

// Moonmere's palette: blue and silver on pale grey stone
const STONE = '#c6ccd6', STONE_D = '#aab2bf', STONE_L = '#dde2ea', CAP = '#d3d8e0';
const BLUE = '#3f6fb0', BLUE_D = '#2f4f80', NAVY = '#26395e', SILVER = '#e6ebf2', GLASS = '#9cc7f0', SLATE = '#5c6f8f';
const MORTAR = 'rgba(62,47,40,0.22)';
export const CASTLE_KINDS = new Set(['wall_h', 'wall_v', 'tower', 'gatehouse', 'keep', 'brazier', 'stone_lamp', 'banner_pole', 'statue', 'market_stall', 'quay_post']);

const shadow = (g: G, rx: number, ry = rx * 0.3) => { g.fillStyle = 'rgba(62,47,40,0.17)'; g.beginPath(); g.ellipse(2, 2, rx, ry, 0, 0, TAU); g.fill(); };
/** Ashlar blocks over a rectangle (running bond). */
function bricks(g: G, x0: number, y0: number, w: number, h: number, row = 14, bw = 24, seed = 1) {
  g.save(); g.beginPath(); g.rect(x0, y0, w, h); g.clip();
  const r = mulberry(seed);
  for (let y = y0 + h, k = 0; y > y0; y -= row, k++) {
    line(g, [[x0, y], [x0 + w, y]], 900 + k, 1.1, MORTAR, 0.2);
    for (let x = x0 + (k % 2 ? bw / 2 : 0); x < x0 + w; x += bw) {
      line(g, [[x, y], [x, y - row]], 950 + k * 7 + x, 1.1, MORTAR, 0.2);
      if (r() < 0.16) { g.fillStyle = r() < 0.5 ? 'rgba(255,255,255,0.18)' : 'rgba(62,47,40,0.06)'; g.fillRect(x + 1, y - row + 1, bw - 2, row - 2); }
    }
  }
  g.restore();
}
/** A row of merlons along the top of a wall, from x0 to x1 with its foot at y. */
function merlons(g: G, x0: number, x1: number, y: number, mw = 13, gap = 10, mh = 15, id = 1) {
  for (let x = x0; x + mw <= x1 + 0.5; x += mw + gap) poly(g, [[x, y - mh], [x + mw, y - mh], [x + mw, y], [x, y]], CAP, id + x, 2);
}
/** A blue banner with a silver crescent, hanging from its top edge. */
function banner(g: G, x: number, y: number, w: number, h: number, col = BLUE, id = 1) {
  poly(g, [[x - w / 2, y], [x + w / 2, y], [x + w / 2, y + h], [x, y + h - w * 0.35], [x - w / 2, y + h]], col, id, 2);
  line(g, [[x - w / 2 + 3, y + 4], [x + w / 2 - 3, y + 4]], id + 1, 1.4, SILVER, 0.2);
  const cy = y + h * 0.42, r = w * 0.24;
  g.fillStyle = SILVER; g.beginPath(); g.arc(x, cy, r, 0, TAU); g.fill();
  g.fillStyle = col; g.beginPath(); g.arc(x + r * 0.45, cy - r * 0.2, r * 0.85, 0, TAU); g.fill();
}
/** A little pennant on a pole. */
function pennant(g: G, x: number, y: number, len = 34, id = 1) {
  line(g, [[x, y], [x, y - len]], id, 2.6, '#6c6460');
  poly(g, [[x, y - len], [x + 26, y - len + 6], [x, y - len + 13]], BLUE, id + 1, 1.8);
  g.fillStyle = SILVER; g.beginPath(); g.arc(x, y - len - 3, 3, 0, TAU); g.fill();
}
/** A window with blue glass in a stone arch (lit warm at night by the scene's lights). */
function archWindow(g: G, x: number, y: number, w = 18, h = 26, id = 1) {
  blob(g, [[x - w / 2, y + h], [x - w / 2, y + w * 0.4], [x, y - 2], [x + w / 2, y + w * 0.4], [x + w / 2, y + h]], GLASS, id, 2, 0.2);
  line(g, [[x, y + 2], [x, y + h - 2]], id + 1, 1.2); line(g, [[x - w / 2 + 2, y + h * 0.55], [x + w / 2 - 2, y + h * 0.55]], id + 2, 1.2);
  line(g, [[x - w / 2 - 3, y + h + 2], [x + w / 2 + 3, y + h + 2]], id + 3, 3, STONE_L, 0.2);
}

/** Sprites for the castle kinds (and the market stall). Null for anything else. */
export function castleSprite(kind: string, v = 0): Spr | null {
  switch (kind) {
    case 'wall_h': return sprite('c-wall_h' + (v % 4), 56, 150, 28, 126, (g: G) => {
      g.fillStyle = 'rgba(62,47,40,0.16)'; g.fillRect(-26, -2, 52, 8);
      g.fillStyle = STONE; g.fillRect(-25, -86, 50, 86); bricks(g, -25, -86, 50, 86, 14, 25, 3 + v);
      g.fillStyle = 'rgba(62,47,40,0.08)'; g.fillRect(-25, -16, 50, 16);
      g.fillStyle = STONE_L; g.fillRect(-25, -96, 50, 10);
      line(g, [[-25, -96], [25, -96]], 1, 2.2, INK, 0.2); line(g, [[-25, -86], [25, -86]], 2, 1.6, INK, 0.2); line(g, [[-25, 0], [25, 0]], 3, 2.2, INK, 0.2);
      merlons(g, -24, 24, -96, 14, 10, 16, 10);
      if (v % 4 === 1) banner(g, 0, -84, 20, 46, BLUE, 30);
      if (v % 4 === 3) { blob(g, [[-5, -60], [5, -60], [5, -44], [-5, -44]], NAVY, 40, 1.6, 0.2); }   // an arrow slit
    });
    case 'wall_v': return sprite('c-wall_v' + (v % 4), 52, 190, 26, 140, (g: G) => {
      // seen from above: the wall-walk running up the screen, merlons on its outer side (v & 2: a right-hand wall),
      // and the stone face only at the bottom end of the run (v & 1)
      const out = v & 2 ? 1 : -1;
      if (v & 1) { g.fillStyle = STONE; g.fillRect(-17, -86, 34, 86); bricks(g, -17, -86, 34, 86, 14, 17, 5); line(g, [[-17, 0], [17, 0]], 1, 2.2, INK, 0.2); line(g, [[-17, -86], [-17, 0]], 2, 2, INK, 0.2); line(g, [[17, -86], [17, 0]], 3, 2, INK, 0.2); }
      g.fillStyle = '#ccd2db'; g.fillRect(-17, -86 - 49, 34, 50);
      g.fillStyle = 'rgba(62,47,40,0.07)'; g.fillRect(out > 0 ? 5 : -17, -135, 12, 50);
      line(g, [[-17, -135], [-17, -86]], 4, 2.2, INK, 0.2); line(g, [[17, -135], [17, -86]], 5, 2.2, INK, 0.2);
      for (let y = -134; y < -88; y += 25) poly(g, [[out * 17 - (out > 0 ? 10 : 0), y], [out * 17 + (out > 0 ? 0 : 10), y], [out * 17 + (out > 0 ? 0 : 10), y + 13], [out * 17 - (out > 0 ? 10 : 0), y + 13]], CAP, 6 + y, 1.8, 0.2);
      if ((v >> 2) % 3 === 0) line(g, [[-out * 10, -120], [-out * 10, -100]], 8, 1, MORTAR, 0.2);
    });
    case 'tower': if (v === 9) return sprite('c-lighthouse', 200, 400, 100, 372, (g: G) => {
      // the Drowned Lighthouse: a tapering white tower in Moonmere-blue bands on its wet rocks, leaning a little,
      // cracked, the lamp room's glass dark but its lamp still glowing sea-blue
      shadow(g, 70, 16);
      blob(g, [[-78, 0], [-66, -26], [-30, -38], [16, -40], [58, -30], [80, -6], [60, 8], [-50, 8]], '#8a929e', 1, 2.4, 0.3);
      for (const [x, y, r] of [[-60, -4, 14], [52, -2, 16], [-20, 2, 10], [70, -14, 9]] as [number, number, number][]) oval(g, x, y, r, r * 0.6, '#9aa3ae', 2 + x, 2);
      g.save(); g.translate(0, -30); g.rotate(0.05);
      const hw = (y: number) => 40 - (-y) * 0.06;   // half-width at height y (y runs 0 down to -270)
      poly(g, [[-hw(0), 0], [hw(0), 0], [hw(-250), -250], [-hw(-250), -250]], '#f1f0ea', 3, 2.4, 0.2);
      for (const [y0, y1] of [[-40, -70], [-120, -150], [-200, -230]] as [number, number][]) poly(g, [[-hw(y0), y0], [hw(y0), y0], [hw(y1), y1], [-hw(y1), y1]], BLUE, 4 - y0, 2, 0.2);
      g.fillStyle = 'rgba(62,47,40,0.1)'; g.beginPath(); g.moveTo(hw(0) - 16, 0); g.lineTo(hw(0), 0); g.lineTo(hw(-250), -250); g.lineTo(hw(-250) - 14, -250); g.fill();
      line(g, [[-14, -96], [-4, -108], [-10, -118], [2, -132]], 20, 1.6, INK, 0.3);   // a long crack
      line(g, [[18, -176], [10, -186], [16, -196]], 21, 1.4, INK, 0.3);
      archWindow(g, 0, -90, 14, 22, 30); archWindow(g, -4, -174, 12, 18, 31);
      blob(g, [[-14, 0], [14, 0], [14, -30], [0, -40], [-14, -30]], '#4a4440', 32, 2, 0.2);   // the door, long since stove in
      // the gallery, the lamp room and its cap
      poly(g, [[-38, -250], [38, -250], [38, -262], [-38, -262]], STONE_L, 40, 2.2, 0.2);
      for (let x = -32; x <= 32; x += 10) line(g, [[x, -262], [x, -276]], 41 + x, 1.6, SLATE);
      line(g, [[-36, -276], [36, -276]], 42, 2, SLATE);
      g.save(); g.globalAlpha = 0.55; oval(g, 0, -292, 46, 34, '#7fe0f0', 43, 0); g.restore();   // the lamp's glow
      poly(g, [[-24, -262], [24, -262], [24, -312], [-24, -312]], NAVY, 44, 2.2, 0.2);
      oval(g, 0, -288, 10, 12, '#bff4ff', 45, 1.6);
      for (const x of [-12, 12]) line(g, [[x, -264], [x, -310]], 46 + x, 1.4, SLATE);
      blob(g, [[-30, -312], [30, -312], [18, -330], [0, -338], [-18, -330]], BLUE_D, 47, 2.4, 0.2);
      line(g, [[0, -338], [0, -350]], 48, 2.2, INK); oval(g, 0, -352, 3.5, 3.5, SILVER, 49, 1.2);
      g.restore();
      // seaweed and the tide line round its foot
      for (const x of [-46, -30, 34, 50]) line(g, [[x, -20], [x + 4, -34], [x - 2, -46]], 60 + x, 2.4, '#3f7a5a', 0.4);
    });
    // falls through for the ordinary towers
    return sprite('c-tower' + (v % 2), 150, 300, 75, 272, (g: G) => {
      shadow(g, 50, 12);
      poly(g, [[-44, -178], [44, -178], [44, 0], [-44, 0]], STONE, 1, 2.4, 0.2); bricks(g, -44, -178, 88, 178, 16, 26, 11 + v);
      g.fillStyle = 'rgba(62,47,40,0.1)'; g.fillRect(20, -178, 24, 178);
      line(g, [[-44, -12], [44, -12]], 2, 1.6, INK, 0.2);
      poly(g, [[-52, -200], [52, -200], [52, -178], [-52, -178]], STONE_L, 3, 2.4, 0.2);
      line(g, [[-50, -186], [50, -186]], 4, 3, BLUE, 0.2);
      merlons(g, -51, 51, -200, 15, 9, 18, 20);
      blob(g, [[-4, -134], [4, -134], [4, -104], [-4, -104]], NAVY, 5, 1.6, 0.2); blob(g, [[-4, -70], [4, -70], [4, -46], [-4, -46]], NAVY, 6, 1.6, 0.2);
      banner(g, -26, -168, 22, 52, v % 2 ? BLUE_D : BLUE, 40); banner(g, 26, -168, 22, 52, v % 2 ? BLUE : BLUE_D, 50);
      pennant(g, 0, -218, 40, 60);
    });
    case 'gatehouse': return sprite('c-gatehouse', 260, 330, 130, 300, (g: G) => {
      shadow(g, 110, 14);
      // the span over the road, then the two towers either side of it
      poly(g, [[-52, -232], [52, -232], [52, -150], [-52, -150]], STONE, 1, 2.4, 0.2); bricks(g, -52, -232, 104, 82, 15, 26, 21);
      blob(g, [[-48, -150], [-48, -110], [-30, -138], [0, -146], [30, -138], [48, -110], [48, -150]], STONE_D, 2, 2.2, 0.2);
      for (let x = -40; x <= 40; x += 13) line(g, [[x, -146 + Math.abs(x) * 0.25], [x, -124 + Math.abs(x) * 0.5]], 3 + x, 2.4, '#4a4440');   // the portcullis, raised
      line(g, [[-44, -126], [44, -126]], 4, 2, '#4a4440');
      merlons(g, -50, 50, -232, 14, 9, 16, 30);
      line(g, [[-50, -214], [50, -214]], 5, 3, BLUE, 0.2);
      banner(g, 0, -206, 30, 50, BLUE, 70);
      for (const sx of [-1, 1]) {
        const x0 = sx * 76;
        poly(g, [[x0 - 28, -262], [x0 + 28, -262], [x0 + 28, 0], [x0 - 28, 0]], STONE, 80 + sx, 2.4, 0.2); bricks(g, x0 - 28, -262, 56, 262, 16, 28, 31 + sx);
        g.fillStyle = 'rgba(62,47,40,0.09)'; g.fillRect(sx > 0 ? x0 + 8 : x0 - 28, -262, 20, 262);
        poly(g, [[x0 - 34, -282], [x0 + 34, -282], [x0 + 34, -262], [x0 - 34, -262]], STONE_L, 82 + sx, 2.2, 0.2);
        merlons(g, x0 - 33, x0 + 33, -282, 13, 8, 16, 90 + sx * 10);
        blob(g, [[x0 - 4, -200], [x0 + 4, -200], [x0 + 4, -172], [x0 - 4, -172]], NAVY, 84 + sx, 1.6, 0.2);
        banner(g, x0, -150, 26, 64, BLUE_D, 100 + sx * 10);
        pennant(g, x0, -298, 36, 120 + sx * 10);
      }
    });
    case 'keep': return sprite('c-keep', 440, 420, 220, 395, (g: G) => {
      g.scale(0.82, 0.82);   // (drawn at full size, then fitted to its 9 x 6 footprint)
      shadow(g, 230, 20);
      // the rise it stands on: a stone terrace with steps up the middle
      poly(g, [[-220, -46], [220, -46], [216, 0], [-216, 0]], STONE_D, 1, 2.4, 0.2); bricks(g, -218, -46, 436, 46, 15, 30, 41);
      for (let s = 0; s < 4; s++) poly(g, [[-34 - s * 4, -46 + s * 12], [34 + s * 4, -46 + s * 12], [34 + s * 4, -34 + s * 12], [-34 - s * 4, -34 + s * 12]], s % 2 ? STONE_L : CAP, 2 + s, 1.8, 0.2);
      // the hall
      poly(g, [[-150, -270], [150, -270], [150, -46], [-150, -46]], STONE, 10, 2.6, 0.2); bricks(g, -150, -270, 300, 224, 16, 30, 43);
      line(g, [[-150, -262], [150, -262]], 11, 4, BLUE, 0.2); line(g, [[-150, -255], [150, -255]], 12, 2, SILVER, 0.2);
      merlons(g, -150, 150, -270, 18, 12, 20, 20);
      for (const x of [-105, -60, 60, 105]) archWindow(g, x, -220, 20, 30, 30 + x);
      for (const x of [-105, 105]) archWindow(g, x, -140, 20, 30, 40 + x);
      // the great door, blue and silver banners either side
      blob(g, [[-30, -46], [-30, -112], [0, -136], [30, -112], [30, -46]], STONE_L, 50, 2.4, 0.2);
      blob(g, [[-22, -46], [-22, -108], [0, -126], [22, -108], [22, -46]], '#4d5f80', 51, 2.2, 0.2);
      line(g, [[0, -122], [0, -48]], 52, 1.4, 'rgba(255,255,255,0.35)');
      for (const y of [-100, -80, -60]) for (const x of [-12, 12]) { g.fillStyle = SILVER; g.beginPath(); g.arc(x, y, 2.4, 0, TAU); g.fill(); }
      banner(g, -62, -250, 34, 92, BLUE, 60); banner(g, 62, -250, 34, 92, BLUE, 70);
      // the side towers, with blue slate cones
      for (const sx of [-1, 1]) {
        const x0 = sx * 180;
        poly(g, [[x0 - 40, -300], [x0 + 40, -300], [x0 + 40, -46], [x0 - 40, -46]], STONE, 80 + sx, 2.4, 0.2); bricks(g, x0 - 40, -300, 80, 254, 16, 26, 47 + sx);
        g.fillStyle = 'rgba(62,47,40,0.09)'; g.fillRect(x0 + 16, -300, 24, 254);
        line(g, [[x0 - 40, -292], [x0 + 40, -292]], 82 + sx, 3, SILVER, 0.2);
        blob(g, [[x0 - 50, -298], [x0 + 50, -298], [x0, -392]], BLUE, 84 + sx, 2.6, 0.3);
        for (let k = 1; k < 4; k++) { const y = -298 - k * 23, hw = 50 * (1 - k / 4); line(g, [[x0 - hw, y], [x0 + hw, y]], 86 + k + sx, 1.4, shade(BLUE, -0.25), 0.2); }
        archWindow(g, x0, -240, 18, 28, 90 + sx); archWindow(g, x0, -160, 18, 28, 95 + sx);
        pennant(g, x0, -392, 34, 100 + sx * 10);
      }
      // the central tower over the hall
      poly(g, [[-46, -350], [46, -350], [46, -270], [-46, -270]], STONE, 120, 2.4, 0.2); bricks(g, -46, -350, 92, 80, 16, 24, 49);
      archWindow(g, 0, -335, 18, 28, 121);
      blob(g, [[-58, -348], [58, -348], [0, -440]], BLUE_D, 122, 2.6, 0.3);
      line(g, [[-58, -350], [58, -350]], 123, 3, SILVER, 0.2);
      g.fillStyle = SILVER; g.beginPath(); g.arc(0, -446, 6, 0, TAU); g.fill(); line(g, [[0, -446], [0, -460]], 124, 2, '#6c6460');
    });
    case 'brazier': return sprite('c-brazier', 60, 100, 30, 88, (g: G) => {
      shadow(g, 16, 5);
      poly(g, [[-9, -34], [9, -34], [12, 0], [-12, 0]], STONE_D, 1, 2.2, 0.2);
      oval(g, 0, -38, 20, 7, '#7d8594', 2, 2.2, 14);
      blob(g, [[-16, -40], [-10, -60], [-4, -48], [0, -74], [6, -50], [11, -62], [16, -40]], '#f28b4b', 3, 1.8, 0.4);
      blob(g, [[-8, -40], [-2, -58], [4, -44], [8, -40]], '#f7d35e', 4, 1.2, 0.3);
    });
    case 'stone_lamp': return sprite('c-stone_lamp', 50, 140, 25, 128, (g: G) => {
      shadow(g, 12, 4);
      poly(g, [[-11, -12], [11, -12], [12, 0], [-12, 0]], STONE_D, 1, 2, 0.2);
      poly(g, [[-5, -84], [5, -84], [6, -12], [-6, -12]], STONE, 2, 2, 0.2);
      poly(g, [[-13, -110], [13, -110], [11, -84], [-11, -84]], GLASS, 3, 2, 0.2);
      g.fillStyle = 'rgba(255,255,255,0.55)'; g.fillRect(-7, -106, 4, 16);
      line(g, [[0, -110], [0, -84]], 4, 1.2); line(g, [[-12, -97], [12, -97]], 5, 1.2);
      poly(g, [[-16, -110], [16, -110], [0, -124]], SLATE, 6, 2, 0.2);
      g.fillStyle = SILVER; g.beginPath(); g.arc(0, -126, 3, 0, TAU); g.fill();
    });
    case 'banner_pole': return sprite('c-banner_pole' + (v % 2), 70, 190, 35, 176, (g: G) => {
      shadow(g, 10, 4);
      oval(g, 0, -4, 9, 4, STONE_D, 1, 1.8, 10);
      line(g, [[0, -4], [0, -160]], 2, 4, '#6c6460');
      line(g, [[-24, -150], [24, -150]], 3, 3, '#6c6460');
      g.fillStyle = SILVER; g.beginPath(); g.arc(0, -164, 5, 0, TAU); g.fill(); g.lineWidth = 1.4; g.strokeStyle = INK; g.stroke();
      banner(g, 0, -148, 40, 78, v % 2 ? BLUE_D : BLUE, 10);
    });
    case 'statue': return sprite('c-statue', 90, 180, 45, 166, (g: G) => {
      shadow(g, 30, 8);
      poly(g, [[-30, -40], [30, -40], [32, 0], [-32, 0]], STONE_D, 1, 2.4, 0.2);
      poly(g, [[-36, -48], [36, -48], [36, -40], [-36, -40]], STONE_L, 2, 2, 0.2);
      line(g, [[-30, -20], [30, -20]], 3, 2.4, BLUE, 0.2);
      // a hornkin stargazer in a long robe, holding up a crescent moon
      blob(g, [[-18, -48], [-14, -100], [0, -112], [14, -100], [18, -48]], '#d9dee6', 4, 2.4, 0.3);
      oval(g, 0, -120, 11, 11, '#d9dee6', 5, 2.2, 12);
      line(g, [[-6, -128], [-12, -140], [-8, -146]], 6, 2.2, INK); line(g, [[6, -128], [12, -140], [8, -146]], 7, 2.2, INK);
      line(g, [[10, -98], [24, -128]], 8, 4, '#d9dee6'); line(g, [[10, -98], [24, -128]], 9, 1.6, INK);
      g.fillStyle = SILVER; g.beginPath(); g.arc(28, -138, 11, 0, TAU); g.fill(); g.lineWidth = 1.8; g.strokeStyle = INK; g.stroke();
      g.fillStyle = '#d9dee6'; g.beginPath(); g.arc(33, -141, 9, 0, TAU); g.fill();
    });
    case 'market_stall': return sprite('c-market_stall' + (v % 2), 160, 160, 80, 128, (g: G) => {
      shadow(g, 64, 12);
      poly(g, [[-66, -42], [66, -42], [66, 0], [-66, 0]], '#b98d62', 1, 2.4, 0.2);
      line(g, [[-64, -30], [64, -30]], 2, 1.4, 'rgba(62,47,40,0.35)');
      for (const x of [-62, 62]) line(g, [[x, -42], [x, -112]], 3 + x, 4, '#8a6a4a');
      const n = 7, top = -118, bot = -96;
      for (let i = 0; i < n; i++) {
        const x0 = -72 + i * 144 / n, x1 = x0 + 144 / n;
        poly(g, [[x0, top], [x1, top], [x1, bot], [x0, bot]], i % 2 ? SILVER : (v % 2 ? BLUE_D : BLUE), 10 + i, 1.8, 0.2);
        g.fillStyle = i % 2 ? SILVER : (v % 2 ? BLUE_D : BLUE); g.beginPath(); g.arc((x0 + x1) / 2, bot, (x1 - x0) / 2, 0, Math.PI); g.fill(); g.lineWidth = 1.6; g.strokeStyle = INK; g.stroke();
      }
      line(g, [[-74, top], [74, top]], 20, 2.4, INK, 0.2);
      // wares: silver fish, blue bottles, pearls, a crate of kelp
      for (let k = 0; k < 3; k++) { const x = -50 + k * 16; blob(g, [[x - 8, -46], [x + 6, -50], [x + 9, -46], [x + 6, -42]], '#c9d3de', 30 + k, 1.4, 0.2); }
      for (let k = 0; k < 3; k++) { const x = 4 + k * 12; poly(g, [[x - 4, -58], [x + 4, -58], [x + 4, -44], [x - 4, -44]], k % 2 ? GLASS : BLUE, 40 + k, 1.4, 0.2); }
      for (let k = 0; k < 4; k++) { g.fillStyle = '#f4eef6'; g.beginPath(); g.arc(46 + (k % 2) * 7, -47 - Math.floor(k / 2) * 6, 3.4, 0, TAU); g.fill(); g.lineWidth = 1; g.strokeStyle = INK; g.stroke(); }
    });
    case 'quay_post': return sprite('c-quay_post', 40, 60, 20, 50, (g: G) => {
      shadow(g, 10, 4);
      poly(g, [[-8, -26], [8, -26], [9, 0], [-9, 0]], '#7d8594', 1, 2, 0.2);
      oval(g, 0, -26, 9, 4, '#9aa2b0', 2, 1.8, 10);
      line(g, [[-9, -16], [9, -14]], 3, 2, '#a77a52');
    });
  }
  return null;
}

/** Where castle props stand (game pixels) and the lights they give. Null for the ones drawn at the usual spot. */
export function castlePropFor(p: PropDef, lamps: Light[]): { x: number; y: number; spr: Spr | null; kind: string }[] | null {
  const TS = 16, bx = p.x * TS, by = p.y * TS, v = p.v ?? 0;
  switch (p.kind as string) {
    case 'brazier': lamps.push({ x: bx + 8, y: by + TS - 2 - 22, r: 52, warm: true, fire: true }); return [{ x: bx + 8, y: by + TS - 2, spr: castleSprite('brazier'), kind: 'brazier' }];
    case 'stone_lamp': lamps.push({ x: bx + 8, y: by + TS - 2 - 34, r: 56 }); return [{ x: bx + 8, y: by + TS - 2, spr: castleSprite('stone_lamp'), kind: 'stone_lamp' }];
    case 'gatehouse': lamps.push({ x: bx + 16, y: by - 40, r: 40, warm: true }); return [{ x: bx + 16, y: by + TS, spr: castleSprite('gatehouse'), kind: 'gatehouse' }];   // centred on the gap (this tile and the next)
    case 'tower': if (v === 9) lamps.push({ x: bx + 8, y: by + TS - 112, r: 90 }); return [{ x: bx + 8, y: by + TS, spr: castleSprite('tower', v), kind: 'tower' }];   // (9: the Drowned Lighthouse, its lamp still lit)
    case 'keep': {   // standing on its rise: p is the middle of the front row of its footprint
      for (const dx of [-60, -20, 20, 60]) lamps.push({ x: bx + 8 + dx, y: by - 50, r: 34, warm: true });
      return [{ x: bx + 8, y: by + TS, spr: castleSprite('keep'), kind: 'keep' }];
    }
    case 'market_stall': lamps.push({ x: bx + 24, y: by - 10, r: 34, warm: true }); return [{ x: bx + 24, y: by + 14, spr: castleSprite('market_stall', v), kind: 'market_stall' }];
    case 'wall_h': case 'wall_v': return [{ x: bx + 8, y: by + TS - 1, spr: castleSprite(p.kind, v), kind: p.kind }];
  }
  return null;
}

/** A stone house: grey ashlar walls, a blue slate roof, blue-glass arched windows and blue-and-silver trim. */
export function stoneHouseSprite(hd: { w: number; h: number; door: number; roof: string; name: string; seed: number }): Spr {
  const w = hd.w * T, total = hd.h * T, wall = Math.min(2.6 * T, total * 0.46), roofH = (total - wall) / 0.85 * 0.92 * 0.85;
  const doorX = (hd.door + 0.5 - hd.w / 2) * T, extra = 1.4 * T;
  return sprite(`c-house-${hd.seed}-${Math.round(w)}`, w + 3 * T, wall + roofH + extra + 40, w / 2 + 1.5 * T, wall + roofH + extra + 20, (g: G) => {
    const r = mulberry(hd.seed);
    g.fillStyle = 'rgba(62,47,40,0.18)'; g.beginPath(); g.ellipse(0, 4, w * 0.56, 12, 0, 0, TAU); g.fill();
    // the roof: blue slate in rows
    const ro = 0.4 * T, roofC = hd.roof;
    poly(g, [[-w / 2 - ro, -wall + 8], [w / 2 + ro, -wall + 8], [w / 2 - ro * 0.3, -wall - roofH], [-w / 2 + ro * 0.3, -wall - roofH]], roofC, 1, 2.6, 0.3);
    for (let k = 1; k < 6; k++) {
      const y = -wall + 8 - (roofH + 8) * k / 6, inset = ro * (1 - k / 6) * 0.7;
      line(g, [[-w / 2 - ro + inset + 4, y], [w / 2 + ro - inset - 4, y]], 2 + k, 1.6, shade(roofC, -0.28), 0.2);
      for (let x = -w / 2 - ro + inset + 14 + (k % 2) * 9; x < w / 2 + ro - inset - 10; x += 18) line(g, [[x, y], [x, y + (roofH + 8) / 6 - 2]], 20 + k * 31 + x, 1, shade(roofC, -0.2), 0.2);
    }
    line(g, [[-w / 2 + ro * 0.3, -wall - roofH], [w / 2 - ro * 0.3, -wall - roofH]], 9, 5, shade(roofC, -0.3));
    const cx = w * 0.27; poly(g, [[cx - 11, -wall - roofH * 0.6], [cx + 11, -wall - roofH * 0.6], [cx + 11, -wall - roofH - 18], [cx - 11, -wall - roofH - 18]], STONE_D, 10, 2.2, 0.2);
    // the walls
    poly(g, [[-w / 2, -wall], [w / 2, -wall], [w / 2, 0], [-w / 2, 0]], STONE, 11, 2.4, 0.2); bricks(g, -w / 2, -wall, w, wall, 14, 26, hd.seed & 255);
    for (const sx of [-1, 1]) for (let y = -wall + 4; y < -4; y += 20) poly(g, [[sx * w / 2 - (sx > 0 ? 14 : 0), y], [sx * w / 2 + (sx > 0 ? 0 : 14), y], [sx * w / 2 + (sx > 0 ? 0 : 14), y + 14], [sx * w / 2 - (sx > 0 ? 14 : 0), y + 14]], STONE_L, 12 + y + sx, 1.4, 0.2);
    g.fillStyle = 'rgba(62,47,40,0.09)'; g.fillRect(-w / 2, -12, w, 12);
    line(g, [[-w / 2 - 2, -wall + 2], [w / 2 + 2, -wall + 2]], 13, 5, BLUE, 0.2); line(g, [[-w / 2, -wall + 7], [w / 2, -wall + 7]], 14, 2, SILVER, 0.2);
    // the door: oak under a stone arch, silver studs
    blob(g, [[doorX - 0.55 * T, 0], [doorX - 0.55 * T, -1.15 * T], [doorX, -1.55 * T], [doorX + 0.55 * T, -1.15 * T], [doorX + 0.55 * T, 0]], STONE_L, 15, 2.2, 0.2);
    blob(g, [[doorX - 0.42 * T, 0], [doorX - 0.42 * T, -1.08 * T], [doorX, -1.38 * T], [doorX + 0.42 * T, -1.08 * T], [doorX + 0.42 * T, 0]], '#7a5a40', 16, 2.2, 0.2);
    line(g, [[doorX, -1.3 * T], [doorX, -4]], 17, 1.4, 'rgba(62,47,40,0.5)');
    for (const y of [-0.9, -0.5]) for (const x of [-0.25, 0.25]) { g.fillStyle = SILVER; g.beginPath(); g.arc(doorX + x * T, y * T, 2.2, 0, TAU); g.fill(); }
    // windows with blue shutters
    const wins = (w > 5.5 * T ? [-w * 0.34, -w * 0.12, w * 0.12, w * 0.34] : [-w * 0.3, w * 0.3]).filter(x => Math.abs(x - doorX) > 0.85 * T);
    wins.forEach((x, i) => { const y = -wall * 0.82; archWindow(g, x, y, 0.62 * T, 0.7 * T, 30 + i * 5);
      for (const sx of [-1, 1]) poly(g, [[x + sx * 0.33 * T, y + 4], [x + sx * 0.5 * T, y + 4], [x + sx * 0.5 * T, y + 0.7 * T], [x + sx * 0.33 * T, y + 0.7 * T]], i % 2 ? BLUE : BLUE_D, 50 + i * 2 + sx, 1.6, 0.2); });
    void r;
    // the name plaque, silver on blue
    if (hd.name) {
      g.font = `600 17px ${I.FONT ?? 'system-ui'}`; const tw = g.measureText(hd.name).width + 30, py = -wall - 6;
      blob(g, [[-tw / 2, py - 14], [tw / 2, py - 14], [tw / 2 + 8, py], [tw / 2, py + 14], [-tw / 2, py + 14], [-tw / 2 - 8, py]], '#eef2f8', 60, 2.2, 0.3);
      line(g, [[-tw / 2 + 4, py + 11], [tw / 2 - 4, py + 11]], 61, 2.4, BLUE, 0.2);
      g.fillStyle = INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(hd.name, 0, py);
    }
  });
}

/** Flagstone paving over every stone tile (the castle city has no cobbles and no grass). */
export function paintFlags(g: G, w: number, h: number, tiles: Uint8Array, stone: number, seed: number): void {
  const TI = 48, r = mulberry(seed * 7 + 3);
  g.save(); g.beginPath();
  for (let ty = 0; ty < h; ty++) for (let tx = 0; tx < w; tx++) if (tiles[ty * w + tx] === stone) g.rect(tx * TI - 1, ty * TI - 1, TI + 2, TI + 2);
  g.clip();
  g.fillStyle = '#cdd2da'; g.fillRect(0, 0, w * TI, h * TI);
  const cols = ['#c5cbd4', '#d3d8df', '#bcc3cd', '#d9dde3', '#c9ced7'];
  for (let y = 0; y < h * TI; y += 22) {
    let x = -(r() * 20);
    while (x < w * TI) {
      const fw = 26 + r() * 22;
      g.fillStyle = cols[Math.floor(r() * cols.length)];
      rrect(g, x + 1.5, y + 1.5, fw - 3, 19, 3); g.fill(); g.lineWidth = 1.1; g.strokeStyle = 'rgba(62,47,40,0.24)'; g.stroke();
      if (r() < 0.06) { g.fillStyle = 'rgba(63,111,176,0.16)'; rrect(g, x + 4, y + 4, fw - 8, 13, 2); g.fill(); }   // a blue-tinted slab now and then
      x += fw;
    }
  }
  g.restore();
}
